import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { CaretDown, CaretRight } from "@phosphor-icons/react";
import { ADMIN_NAV, ADMIN_NAV_GROUPS, ADMIN_ACCENT } from "@/pages/admin/adminNav";
import { useFollowUpCounts } from "@/hooks/useFollowUpCounts";
import { useActionCounts } from "@/hooks/useActionCounts";
import { DESKTOP_QUERY, useMediaQuery } from "@/hooks/useMediaQuery";

/**
 * AdminSidebar — desktop-only grouped navigation for the admin role.
 *
 * Pinta ADMIN_NAV_GROUPS. No declara rutas ni etiquetas: las referencia por
 * `key` desde adminNav.js, que es el registro. Un grupo puede contener una
 * `key` suelta o un nodo con hijos —"Operaciones" es lo segundo— y el nodo se
 * despliega igual que un grupo, con su propia memoria.
 *
 * Groups are now collapsible per user (click the header). The group that
 * contains the active route auto-opens; the active item never hides behind
 * a collapsed group. Persisted in `localStorage` under `admin.sidebar.open`
 * so the choice survives page navigations within the session.
 *
 * The mobile/tablet experience continues to use the horizontal scrolling
 * tab strip rendered by Shell.jsx — this component is hidden below `lg`.
 */
const STORAGE_KEY = "admin.sidebar.open";

function loadOpen(defaultOpen) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultOpen;
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) return new Set(arr);
  } catch { /* ignore malformed value */ }
  return defaultOpen;
}

export default function AdminSidebar() {
  const location = useLocation();
  const byKey = Object.fromEntries(ADMIN_NAV.map((n) => [n.key, n]));
  // Fetch only for the follow-ups badge. Silent-on-error and one-shot at
  // mount — never blocks the sidebar. `counts` is null while loading or on
  // failure, in which case the badge is simply not rendered. Below `lg`
  // the sidebar is CSS-hidden and MobileBottomNav owns the same badge, so
  // the request is only made where this sidebar is actually visible.
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const { counts: followUpCounts } = useFollowUpCounts({ enabled: desktop });
  const followUpBadge = followUpCounts
    ? (followUpCounts.overdue || 0) + (followUpCounts.due_today || 0)
    : 0;
  // Action Center badge — critical + today + overdue. Independent
  // request (counts_only=true) so this stays cheap on every sidebar
  // mount. Silent on failure.
  const { badge: actionBadge } = useActionCounts();

  const isActive = (to) =>
    location.pathname === to ||
    (to !== "/dashboard" && location.pathname.startsWith(to + "/"));

  // Which group holds the currently active route — this group must be open
  // regardless of user preference so the active item is never hidden.
  const activeGroupKey = useMemo(() => {
    for (const g of ADMIN_NAV_GROUPS) {
      for (const entry of g.items) {
        const keys = typeof entry === "string" ? [entry] : entry.items;
        for (const k of keys) {
          const it = byKey[k];
          if (it && isActive(it.to)) return g.key;
        }
      }
    }
    return null;
    // isActive closes over location.pathname
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Default: open every group. Users can collapse; the choice persists
  // (localStorage). The active group is forced open at render time.
  const [openGroups, setOpenGroups] = useState(() =>
    loadOpen(new Set([
      ...ADMIN_NAV_GROUPS.map((g) => g.key),
      // Los nodos con hijos también empiezan abiertos: un grupo que oculta
      // la mitad de sus destinos el primer día no es navegación, es un
      // acertijo.
      ...ADMIN_NAV_GROUPS.flatMap((g) => g.items)
        .filter((it) => typeof it !== "string").map((it) => it.key),
    ])),
  );
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...openGroups]));
    } catch { /* private mode / quota exceeded — non-fatal */ }
  }, [openGroups]);

  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const badgeFor = (key, active) => {
    const count = key === "followUps" ? followUpBadge : key === "actionCenter" ? actionBadge : 0;
    if (count <= 0) return null;
    const label = key === "followUps"
      ? `${count} seguimientos por atender`
      : `${count} acciones por atender`;
    const testId = key === "followUps"
      ? "admin-sidebar-followups-badge"
      : "admin-sidebar-action-center-badge";
    return (
      <span
        className={`ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-medium ${
          active ? "bg-white/20 text-white" : "bg-red-500 text-white"
        }`}
        data-testid={testId}
        aria-label={label}
      >
        {count > 99 ? "99+" : count}
      </span>
    );
  };

  /** Una entrada normal: un destino real del registro. */
  const renderItem = (itemKey, nested = false) => {
    const item = byKey[itemKey];
    if (!item) return null;
    const Icon = item.icon;
    const active = isActive(item.to);
    return (
      <li key={item.key}>
        <NavLink
          to={item.to}
          data-testid={`admin-sidebar-nav-${item.key}`}
          aria-current={active ? "page" : undefined}
          className={`group flex items-center gap-2.5 ${nested ? "pl-8 pr-3" : "px-3"} py-2 rounded-md text-sm transition-colors ${
            active
              ? "bg-stone-900 text-white"
              : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
          }`}
          style={active ? { background: ADMIN_ACCENT } : undefined}
        >
          {Icon && (
            <Icon size={16} weight={active ? "fill" : "regular"} className="shrink-0" />
          )}
          <span className="truncate flex-1">{item.label}</span>
          {badgeFor(item.key, active)}
        </NavLink>
      </li>
    );
  };

  /**
   * Una entrada con hijos. No navega a ningún sitio porque no hay ninguna
   * pantalla detrás: abre y cierra los destinos que sí existen. Se fuerza
   * abierta cuando uno de sus hijos es la ruta actual, para que nunca quede
   * escondido el sitio donde estás.
   */
  const renderBranch = (node) => {
    const childActive = node.items.some((k) => byKey[k] && isActive(byKey[k].to));
    const open = openGroups.has(node.key) || childActive;
    const Icon = node.icon;
    const bodyId = `admin-sidebar-body-${node.key}`;
    return (
      <li key={node.key}>
        <button
          type="button"
          onClick={() => toggleGroup(node.key)}
          aria-expanded={open}
          aria-controls={bodyId}
          data-testid={`admin-sidebar-branch-${node.key}`}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-stone-600 hover:bg-stone-100 hover:text-stone-900 transition-colors"
        >
          {Icon && <Icon size={16} className="shrink-0" />}
          <span className="truncate flex-1 text-left">{node.label}</span>
          {open
            ? <CaretDown size={10} weight="bold" />
            : <CaretRight size={10} weight="bold" />}
        </button>
        {open && (
          <ul id={bodyId} className="space-y-0.5 mt-0.5">
            {node.items.map((k) => renderItem(k, true))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <aside
      className="hidden lg:block lg:w-56 shrink-0"
      aria-label="Navegación admin"
      data-testid="admin-sidebar"
    >
      {/* Alto acotado y scroll propio. La barra es `sticky`, así que se queda
          pegada a 5rem del borde: si mide más que la ventana, su parte de
          abajo no se alcanza NUNCA, por mucho que se baje la página. Con
          cinco grupos pasa en un portátil de 900px de alto, y lo que quedaba
          fuera eran Ajustes y todo «Dirección y sistema». */}
      <nav className="sticky top-20 space-y-2 max-h-[calc(100vh-6rem)] overflow-y-auto overscroll-contain pb-2">
        {ADMIN_NAV_GROUPS.map((group) => {
          const isOpen = openGroups.has(group.key) || group.key === activeGroupKey;
          const bodyId = `admin-sidebar-body-${group.key}`;
          return (
          <div key={group.key} data-testid={`admin-sidebar-group-${group.key}`}>
            <button
              type="button"
              onClick={() => toggleGroup(group.key)}
              aria-expanded={isOpen}
              aria-controls={bodyId}
              className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 transition-colors"
              data-testid={`admin-sidebar-group-toggle-${group.key}`}
            >
              <span>{group.label}</span>
              {isOpen
                ? <CaretDown size={10} weight="bold" />
                : <CaretRight size={10} weight="bold" />}
            </button>
            {isOpen && (
            <ul id={bodyId} className="space-y-0.5 mb-3">
              {group.items.map((entry) => (
                typeof entry === "string"
                  ? renderItem(entry)
                  : renderBranch(entry)
              ))}
            </ul>
            )}
          </div>
          );
        })}
      </nav>
    </aside>
  );
}
